import { CheckCircle } from 'lucide-react';
import type { AnswerCardProps, Question } from '../types';
import { typeNames, subTypeNames } from '../../../constants/questionTypes';
import { computeContentKey } from '../../../utils/questionTag';

const getQuestionTypeName = (type: string, subType?: string) => {
  if (type === 'scratch_coding') return 'Scratch编程题';
  if (type === 'python_coding') return 'Python编程题';
  if (type === 'cpp_coding') return 'C++编程题';
  if (type === 'coding' && subType) return subTypeNames[subType] || '主观题';
  return typeNames[type] || '单选题';
};

const groupQuestionsByType = (examQuestions: Question[]) => {
  const groups: { type: string; name: string; indices: number[] }[] = [];
  // 与 PaperEditor 保持一致的排序：Python 在前，Scratch 在后
  const typeOrder = ['single_choice', 'judge', 'multi_choice', 'python_coding', 'cpp_coding', 'scratch_coding', 'coding'];
  examQuestions.forEach((q, idx) => {
    let groupType = q.type;
    if (q.type === 'coding' && q.subType) groupType = `${q.subType}_coding`;
    const existingGroup = groups.find(g => g.type === groupType);
    if (existingGroup) existingGroup.indices.push(idx);
    else groups.push({ type: groupType, name: getQuestionTypeName(q.type, q.subType), indices: [idx] });
  });
  return groups.sort((a, b) => {
    const indexA = typeOrder.indexOf(a.type);
    const indexB = typeOrder.indexOf(b.type);
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
};

const getQuestionStatus = (
  idx: number,
  examQuestions: Question[],
  answers: Record<string, string>,
  isSubmitted: boolean,
  revealedQuestions?: Set<string>,
  isPracticeMode?: boolean
) => {
  const q = examQuestions[idx];
  if (!q) return 'unanswered';

  // 练习模式：使用逐题揭示
  if (isPracticeMode && revealedQuestions) {
    const isRevealed = revealedQuestions.has(q.id);
    if (isRevealed) {
      const ans = answers[q.id];
      if (!ans) return 'wrong';
      if (q.type === 'multi_choice') {
        const selectedIds = ans.split(',');
        const correctIds = q.options?.filter(o => o.isCorrect).map(o => o.id) || [];
        const isCorrect = selectedIds.length === correctIds.length && selectedIds.every(id => correctIds.includes(id));
        return isCorrect ? 'correct' : 'wrong';
      }
      if (q.type === 'coding' || q.type === 'scratch_coding') {
        const aiScore = parseFloat(answers[`${q.id}_aiScore`] || '0');
        return aiScore >= q.score * 0.6 ? 'correct' : 'wrong';
      }
      const selectedOption = q.options?.find(o => o.id === ans);
      return selectedOption?.isCorrect ? 'correct' : 'wrong';
    }
    return answers[q.id] ? 'answered' : 'unanswered';
  }

  // 正常模式：使用全局 isSubmitted
  if (isSubmitted) {
    const ans = answers[q.id];
    if (!ans) return 'wrong';
    if (q.type === 'multi_choice') {
      const selectedIds = ans.split(',');
      const correctIds = q.options?.filter(o => o.isCorrect).map(o => o.id) || [];
      const isCorrect = selectedIds.length === correctIds.length && selectedIds.every(id => correctIds.includes(id));
      return isCorrect ? 'correct' : 'wrong';
    }
    if (q.type === 'coding' || q.type === 'scratch_coding') {
      const aiScore = parseFloat(answers[`${q.id}_aiScore`] || '0');
      return aiScore >= q.score * 0.6 ? 'correct' : 'wrong';
    }
    const selectedOption = q.options?.find(o => o.id === ans);
    return selectedOption?.isCorrect ? 'correct' : 'wrong';
  }
  return answers[q.id] ? 'answered' : 'unanswered';
};

const OBJECTIVE_TYPES = ['single_choice', 'multi_choice', 'judge'];

export default function AnswerCard({
  examQuestions,
  currentIndex,
  answers,
  markedQuestions,
  isSubmitted,
  paper,
  revealedQuestions,
  isPracticeMode,
  onQuestionSelect,
  onCloseSidebar,
  onGroupScoreChange,
  taggedContentKeys,
  ungradedQuestionIds,
  gradedScores,
}: AnswerCardProps & { onGroupScoreChange?: (groupType: string, newScorePerQuestion: number) => void; taggedContentKeys?: Set<string>; ungradedQuestionIds?: Set<string>; gradedScores?: Record<string, number> }) {
  // 编程题教师阅卷得分（questionFeedbacks[q.id].score）：已批阅的编程题按得分率判定绿/红，
  // 得分率≥60% 计正确（与项目编程题正确率统计口径一致）；未批阅仍由 ungradedQuestionIds 黄色高亮兜底
  // 完成度综合判定（与试卷列表页三列进度环口径一致）：
  // 客观题必须同时满足【有参考答案 referenceAnswer】+【有解析 explanation】+【知识点标签指纹命中】三项才算完成；
  // 任一缺失即未完成（题号紫色）。非客观题（编程/主观）仍按标签单条件判定。
  // 注：taggedContentKeys 未传（普通学生作答场景）时不启用该着色，保持原有答题卡行为。
  const checkQuestionCompletion = (idx: number): { complete: boolean; missing: string[] } => {
    const q = examQuestions[idx];
    if (!q || !taggedContentKeys) return { complete: true, missing: [] };
    const contentKey = computeContentKey({ type: q.type, content: q.content, title: (q as any).title, score: q.score });
    const missing: string[] = [];
    if (!taggedContentKeys.has(contentKey)) missing.push('知识点标签');
    if (OBJECTIVE_TYPES.includes(q.type)) {
      const hasAns = !!(q.referenceAnswer && String(q.referenceAnswer).trim());
      const hasExp = !!(q.explanation && String(q.explanation).trim());
      if (!hasAns) missing.push('参考答案');
      if (!hasExp) missing.push('解析');
    }
    return { complete: missing.length === 0, missing };
  };
  const groupedQuestions = groupQuestionsByType(examQuestions);
  const typeLabels = ['一', '二', '三', '四', '五'];
  const answeredCount = Object.keys(answers).filter(key => !key.includes('_')).length;
  const markedCount = markedQuestions.size;

  const getGroupTotalScore = (group: { type: string; name: string; indices: number[] }) =>
    group.indices.reduce((sum, idx) => sum + (examQuestions[idx]?.score || 0), 0);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 border-b border-blue-200">
        <div className="flex items-center justify-between mb-2">
          <span className="font-bold flex items-center gap-2 text-slate-700">
            <CheckCircle className="w-4 h-4" />答题卡
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <span className="text-gray-600">
              已答: <span className="font-bold text-indigo-600">{answeredCount}</span>/{examQuestions.length}
            </span>
            <span className="text-gray-600">
              标记: <span className="font-bold text-amber-600">{markedCount}</span>
            </span>
          </div>
          <span className="text-gray-600 text-base">
            总分: <span className="font-bold text-green-600">{paper?.total_score || 0}</span>分
          </span>
        </div>
      </div>
      <div className="p-4 max-h-[60vh] overflow-y-auto">
        {groupedQuestions.map((group, groupIdx) => {
          const groupTotalScore = getGroupTotalScore(group);
          const questionCount = group.indices.length;
          // 计算每题平均分（用于下拉框默认值）
          const avgScorePerQuestion = questionCount > 0 ? Math.round(groupTotalScore / questionCount) : 0;
          // 检查该题型下所有题目分值是否一致
          const allScoresSame = group.indices.every(idx => examQuestions[idx]?.score === examQuestions[group.indices[0]]?.score);
          const scoreOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20, 25, 30];
          // 只有当所有题目分值一致且平均值在选项中时，才设置下拉框默认值
          const selectValue = allScoresSame && scoreOptions.includes(avgScorePerQuestion) ? String(avgScorePerQuestion) : '';

          return (
            <div key={group.type} className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-bold text-gray-800">
                  {typeLabels[groupIdx]}、{group.name}（共{questionCount}题，{groupTotalScore}分）
                </span>
                {onGroupScoreChange && (
                  <select
                    value={selectValue}
                    onChange={(e) => onGroupScoreChange(group.type, parseInt(e.target.value))}
                    className="px-2 py-0.5 text-xs border border-gray-300 rounded bg-white hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    title={allScoresSame ? "设置每题分值" : "该题型题目分值不一致，请选择统一分值"}
                  >
                    <option value="" disabled>-</option>
                    {scoreOptions.map(score => (
                      <option key={score} value={score}>{score}分</option>
                    ))}
                  </select>
                )}
              </div>
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
              {group.indices.map((idx) => {
                const q = examQuestions[idx];
                // 已批阅编程题：优先按教师阅卷得分判定对错（覆盖 answers 里缺失的 _aiScore 判分口径）
                let status: string;
                const PROGRAMMING_TYPES = ['scratch_coding', 'python_coding', 'cpp_coding', 'coding'];
                const gradedScore = PROGRAMMING_TYPES.includes(q?.type || '') ? gradedScores?.[q?.id || ''] : undefined;
                if (gradedScore != null && q) {
                  status = gradedScore >= (q.score || 0) * 0.6 ? 'correct' : 'wrong';
                  console.log('[复盘-答题卡] 题号', idx + 1, '编程题已批阅, 阅卷得分:', gradedScore, '判定:', status);
                } else {
                  status = getQuestionStatus(idx, examQuestions, answers, isSubmitted, revealedQuestions, isPracticeMode);
                }
                const isCurrent = idx === currentIndex;
                const isMarked = markedQuestions.has(idx);
                const isAnswered = status === 'answered' || status === 'correct' || status === 'wrong';

                // 练习模式或已提交：显示对错结果
                const showResult = (isPracticeMode && revealedQuestions?.has(examQuestions[idx]?.id)) || isSubmitted;
                // 未完成（缺标签/参考答案/解析任一项）的题号显示紫色，三项齐备恢复绿色（仅标注场景生效）
                const completion = checkQuestionCompletion(idx);
                const incomplete = !completion.complete;
                const completeNormalClass = taggedContentKeys ? 'text-green-700' : 'text-gray-900';
                if (showResult) {
                  const isCorrect = status === 'correct';
                  // 未阅卷编程题：黄色高亮（bg-yellow-50 底 + 琥珀色文字/边框），教师/学员可一眼识别等待批阅的编程题
                  const isUngraded = ungradedQuestionIds?.has(examQuestions[idx]?.id);
                  console.log('[复盘-答题卡] 题号', idx + 1, '未批阅编程题高亮:', !!isUngraded);
                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        onQuestionSelect(idx);
                        onCloseSidebar?.();
                      }}
                      className={`aspect-square w-full text-sm font-medium rounded-lg border-2 flex items-center justify-center ${
                        isUngraded
                          ? 'bg-yellow-50 text-amber-600 border-amber-400'
                          : isCorrect
                          ? 'bg-green-500 text-white border-green-500'
                          : 'bg-red-500 text-white border-red-500'
                      } ${incomplete && !isCorrect ? 'text-purple-600' : ''}`}
                      title={isUngraded ? '该题为编程题，已提交待教师阅卷' : undefined}
                    >
                      {String(idx + 1).padStart(2, '0')}
                    </button>
                  );
                }

                return (
                  <button
                    key={idx}
                    onClick={() => {
                      onQuestionSelect(idx);
                      onCloseSidebar?.();
                    }}
                    className={`aspect-square w-full text-sm font-medium rounded-lg border-2 transition-all flex items-center justify-center ${
                      isCurrent
                        ? 'border-red-500 ' + (isAnswered ? 'bg-blue-500 text-white' : incomplete ? 'bg-white text-purple-600' : `bg-white ${completeNormalClass}`)
                        : isAnswered
                        ? 'bg-blue-500 text-white border-blue-500'
                        : incomplete
                        ? 'bg-white text-purple-600 border-purple-400 hover:bg-purple-50'
                        : taggedContentKeys
                        ? 'bg-white text-green-700 border-green-300 hover:bg-green-50'
                        : 'bg-white text-gray-900 border-gray-300 hover:bg-blue-50'
                    }`}
                    title={incomplete ? `该题未完成，缺少：${completion.missing.join('、')}` : taggedContentKeys ? '该题已完成（答案/解析/标签齐备）' : undefined}
                  >
                    {isMarked ? <span className="text-lg font-black">?</span> : String(idx + 1).padStart(2, '0')}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      </div>
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        {isSubmitted || isPracticeMode ? (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-green-500 border-2 border-green-500 rounded-lg flex items-center justify-center text-white text-xs font-medium">
                01
              </div>
              <span className="text-gray-600">答对</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-red-500 border-2 border-red-500 rounded-lg flex items-center justify-center text-white text-xs font-medium">
                02
              </div>
              <span className="text-gray-600">答错/未答</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center text-white text-xs font-medium">
                01
              </div>
              <span className="text-gray-600">已答</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-white border border-gray-300 rounded-lg flex items-center justify-center text-gray-900 text-xs font-medium">
                03
              </div>
              <span className="text-gray-600">未答</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-white border-2 border-red-500 rounded-lg flex items-center justify-center text-gray-900 text-xs font-medium">
                08
              </div>
              <span className="text-gray-600">当前题目</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-white border-2 border-amber-500 rounded-lg flex items-center justify-center text-amber-600 text-xs font-medium">
                ？
              </div>
              <span className="text-gray-600">已标记</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}